// ============================================================================
// STAKEHOLDER FORM COMPONENTS
// Reusable form elements shared across contact management modules
// Extracted from StakeholderOnboardingModal and ContactSettings
// ============================================================================

import React from 'react';
import { ChevronRight } from 'lucide-react';

// ============================================================================
// TEXT INPUT
// ============================================================================

export function TextInput({
  label,
  value,
  onChange,
  required,
  placeholder,
  type = 'text',
  className = '',
  disabled,
  readOnly,
}: {
  label: string;
  value: string | number | null;
  onChange: (v: string) => void;
  required?: boolean;
  placeholder?: string;
  type?: string;
  className?: string;
  disabled?: boolean;
  readOnly?: boolean;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <input
        type={type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm ${
          readOnly
            ? 'bg-gray-50 dark:bg-gray-900/60 cursor-not-allowed focus:ring-0'
            : 'bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500'
        } disabled:opacity-50`}
      />
    </div>
  );
}

// ============================================================================
// SELECT INPUT
// ============================================================================

export function SelectInput({
  label,
  value,
  onChange,
  options,
  required,
  placeholder,
  className = '',
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
  required?: boolean;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm ${
          disabled
            ? 'bg-gray-50 dark:bg-gray-900/60 cursor-not-allowed opacity-75'
            : 'bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500'
        }`}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ============================================================================
// TEXTAREA INPUT
// ============================================================================

export function TextareaInput({
  label,
  value,
  onChange,
  rows = 3,
  placeholder,
  className = '',
  readOnly,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  placeholder?: string;
  className?: string;
  readOnly?: boolean;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        readOnly={readOnly}
        className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm ${
          readOnly
            ? 'bg-gray-50 dark:bg-gray-900/60 cursor-not-allowed focus:ring-0'
            : 'bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500'
        }`}
      />
    </div>
  );
}

// ============================================================================
// SECTION HEADER
// ============================================================================

export function SectionHeader({ icon: Icon, title, count }: { icon: React.ElementType; title: string; count?: number }) {
  return (
    <div className="flex items-center gap-2 mb-4">
      <Icon className="w-5 h-5 text-blue-500" />
      <h3 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h3>
      {count !== undefined && (
        <span className="ml-auto text-xs font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
          {count}
        </span>
      )}
    </div>
  );
}

// ============================================================================
// ACCORDION CARD
// ============================================================================

export function AccordionCard({
  icon: Icon,
  title,
  description,
  isOpen,
  onToggle,
  badge,
  children,
}: {
  icon: React.ElementType;
  title: string;
  description?: string;
  isOpen: boolean;
  onToggle: () => void;
  badge?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-gray-900 dark:text-white">{title}</div>
          {description && <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</div>}
        </div>
        {badge && (
          <span className="hidden sm:inline-flex text-[10px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-full">
            {badge}
          </span>
        )}
        <ChevronRight className={`w-4 h-4 text-gray-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
      </button>
      {isOpen && <div className="px-4 pb-4 pt-1 border-t border-gray-100 dark:border-gray-700/50">{children}</div>}
    </div>
  );
}

// ============================================================================
// TOGGLE SETTING ITEM
// ============================================================================

export interface ToggleSettingItemProps {
  icon: React.ElementType;
  label: string;
  description: string;
  value: boolean;
  onChange: (value: boolean) => void;
  isDark: boolean;
  isRequired?: boolean;
}

export function ToggleSettingItem({
  icon: Icon,
  label,
  description,
  value,
  onChange,
  isDark,
  isRequired
}: ToggleSettingItemProps) {
  return (
    <div className={`p-4 rounded-lg border ${
      isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Icon className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
          <div>
            <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {label}
              {isRequired && value && (
                <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">
                  Required
                </span>
              )}
            </p>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {description}
            </p>
          </div>
        </div>
        <button
          onClick={() => onChange(!value)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            value
              ? 'bg-blue-600'
              : isDark ? 'bg-gray-600' : 'bg-gray-300'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              value ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );
}
